import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { exportPlot } from '../utils/plotExport';
import { desktopPlotData, desktopPlotLayout } from './ThemedPlot';
import './PlotExportDialog.css';

export default function PlotExportDialog({ graph, options, onClose }) {
  const [format, setFormat] = useState(['png', 'svg', 'pdf'].includes(options.format) ? options.format : 'png');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.showModal();
    return () => previous?.focus();
  }, []);
  const save = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      // SVG renderers keep scatter points/lines as vectors in SVG and PDF.
      // Heatmaps and image traces retain their native raster representation.
      const vector = format !== 'png';
      const figure = { data: desktopPlotData(graph.data, vector),
        layout: desktopPlotLayout(graph.data, graph.layout, vector) };
      await exportPlot(figure, { ...options, format,
        width: options.width || graph._fullLayout?.width || 800,
        height: options.height || graph._fullLayout?.height || 600 });
      onClose();
    } catch (failure) {
      setError(failure.message || 'Unable to save image. Please try again.');
    } finally { setBusy(false); }
  };
  // Portals retain React ancestry: keep dialog gestures out of canvas handlers.
  const stopCanvasEvent = event => event.stopPropagation();
  return createPortal(<dialog ref={dialog} className="plot-export-dialog"
    onPointerDown={stopCanvasEvent} onMouseDown={stopCanvasEvent}
    onClick={stopCanvasEvent} onKeyDown={stopCanvasEvent}
    aria-labelledby="plot-export-title" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <form onSubmit={save}>
      <h2 id="plot-export-title">Download image</h2>
      <p>{options.filename}</p>
      <label>Format <select value={format} disabled={busy} onChange={event => setFormat(event.target.value)}>
        <option value="png">PNG</option><option value="svg">SVG (vector)</option><option value="pdf">PDF (vector)</option>
      </select></label>
      {error && <p role="alert">{error}</p>}
      <div><button type="button" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Download'}</button></div>
    </form>
  </dialog>, document.body);
}
